import { z } from 'zod/v4'

import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

import { adminPaginationSchema } from './users.screen.js'

/*
 * «Activos»: the asset-type tabs and one page of global assets (`admin.assets`), and one asset with the catalogue of
 * types for its form (`admin.assets.detail`). The row (`AdminAssetRow`) is the one type behind the backend
 * `AssetListItem` and the frontend `Asset`.
 */

/** The tab that shows every asset, and the one that shows those whose type is not in the catalogue. */
export const ASSET_LIST_TAB_ALL = 'all'
export const ASSET_LIST_TAB_UNCATALOGUED = '__uncatalogued__'

export const adminAssetRowSchema = z.strictObject({
  id: z.string(),
  canonicalId: z.string(),
  name: z.string(),
  symbol: z.string(),
  type: z.string(),
  decimals: z.number().int(),
  category: z.string().nullable(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  sortOrder: z.number().int().nullable(),
  coingeckoId: z.string().nullable(),
  chain: z.string().nullable(),
  chainId: z.number().int().nullable(),
  contractAddress: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const assetTypeOptionSchema = z.strictObject({
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  sortOrder: z.number(),
})

/**
 * What the tab of the URL may say. An unknown tab is not an error: the page serves «all» and says it in `appliedTab`
 * (a bookmark of a type that no longer exists shows the whole list, as the page did before).
 */
export const adminAssetsQuerySchema = z.strictObject({
  tab: z.string().min(1).max(64).default(ASSET_LIST_TAB_ALL),
  search: z.string().min(1).max(100).optional(),
  status: z.enum(['active', 'all']).default('active'),
  page: z.coerce.number<number>().int().min(1).default(1),
  limit: z.coerce.number<number>().int().min(1).max(100).default(25),
})

export const adminAssetsScreen = defineScreen({
  id: 'admin.assets',
  path: '/api/screens/admin/assets',
  access: 'super_admin',
  query: adminAssetsQuerySchema,
  response: z.strictObject({
    types: screenSection(z.array(assetTypeOptionSchema)),
    page: screenSection(
      z.strictObject({
        items: z.array(adminAssetRowSchema),
        pagination: adminPaginationSchema,
        appliedTab: z.string(),
      }),
    ),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.assets'],
  budget: { requests: 1, statements: 8, bytes: 40_000, p95Ms: 200 },
  summary: 'Assets: the asset-type tabs and one page of global assets',
})

/** A form of the asset on a chain: its own contract (`catalogue`) or a deployment elsewhere (`alias`, withdrawable). */
export const assetRepresentationSchema = z.strictObject({
  /** The alias id; `null` for the asset's own chain + contract (edited in the asset form). */
  id: z.string().nullable(),
  chain: z.string(),
  chainName: z.string().nullable(),
  contract: z.string(),
  origin: z.enum(['catalogue', 'alias']),
})

/** One validity window of a global ticker alias; `own` = it names THIS asset. Dates are `YYYY-MM-DD`, `null` = open. */
export const assetAliasWindowSchema = z.strictObject({
  id: z.string(),
  assetId: z.string(),
  assetSymbol: z.string(),
  assetName: z.string(),
  validFrom: z.string().nullable(),
  validUntil: z.string().nullable(),
  priority: z.number().int(),
  own: z.boolean(),
})

/** A ticker of a venue / provider namespace this asset holds, with every window of that ticker (any asset). */
export const assetTickerAliasSchema = z.strictObject({
  source: z.string(),
  ticker: z.string(),
  windows: z.array(assetAliasWindowSchema),
})

export const assetAliasSourceSchema = z.strictObject({
  slug: z.string(),
  name: z.string(),
  kind: z.enum(['chain', 'venue', 'price']),
})

export const adminAssetDetailScreen = defineScreen({
  id: 'admin.assets.detail',
  path: '/api/screens/admin/assets/detail',
  access: 'super_admin',
  query: z.strictObject({ id: z.guid() }),
  response: z.strictObject({
    asset: adminAssetRowSchema,
    types: screenSection(z.array(assetTypeOptionSchema)),
    representations: screenSection(z.array(assetRepresentationSchema)),
    aliases: screenSection(z.array(assetTickerAliasSchema)),
    aliasSources: screenSection(z.array(assetAliasSourceSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.assets'],
  budget: { requests: 1, statements: 10, bytes: 40_000, p95Ms: 150 },
  summary: 'Assets › one asset, its chain representations and its ticker aliases with validity windows',
})

export type AdminAssetRow = z.infer<typeof adminAssetRowSchema>
export type AssetTypeOption = z.infer<typeof assetTypeOptionSchema>
export type AdminAssetsQuery = ScreenQuery<typeof adminAssetsScreen>
export type AdminAssetsData = ScreenData<typeof adminAssetsScreen>
export type AdminAssetDetailData = ScreenData<typeof adminAssetDetailScreen>
export type AssetRepresentation = z.infer<typeof assetRepresentationSchema>
export type AssetAliasWindow = z.infer<typeof assetAliasWindowSchema>
export type AssetTickerAlias = z.infer<typeof assetTickerAliasSchema>
export type AssetAliasSource = z.infer<typeof assetAliasSourceSchema>
